import type { CSSProperties, ReactNode } from 'react';

import type { CreateDrawerStep } from './createDrawer.types';
import { DrawerLeaf } from './DrawerLeaf';
import { useRailLineGeometry } from './useRailLineGeometry';

const NODE_GAP = 26;

export function DrawerRail({
  steps,
  cur,
  go,
  railNote,
}: {
  steps: CreateDrawerStep[];
  cur: number;
  go: (i: number) => void;
  railNote?: ReactNode;
}) {
  // Fill reflects COMPLETED steps only: on step `cur` (0-indexed) of N, the
  // line covers `cur` of the `N-1` inter-node segments — 0% on the first step,
  // exactly the active node, never past the last node (100% only at step N).
  const segments = Math.max(steps.length - 1, 0);
  const fillPct = segments > 0 ? Math.min(cur, segments) / segments : 0;
  // Line geometry is measured from the real node centers (see hook) so the track
  // spans node-1 → node-N exactly and the fill lands on the active node.
  const { navRef, lineGeom } = useRailLineGeometry(steps.length);
  const stepState = (i: number) => (cur > i ? 'done' : cur === i ? 'active' : 'todo');
  return (
    <nav className="nb-cdrawer-rail" ref={navRef}>
      <div className="nb-cdrawer-track" style={lineGeom} />
      <div
        className="nb-cdrawer-fill"
        style={{ ...lineGeom, ['--cd-fill' as string]: String(fillPct) } as CSSProperties}
      />
      <div
        className="nb-cdrawer-steps"
        style={{ position: 'relative', display: 'flex', flexDirection: 'column', gap: NODE_GAP }}
      >
        {steps.map((s, i) => (
          <button
            key={s.id || i}
            className="nb-cdrawer-stepbtn"
            data-state={stepState(i)}
            onClick={() => go(i)}
          >
            <span className="nb-cdrawer-node">
              {cur > i ? <DrawerLeaf size={14} /> : <span>{i + 1}</span>}
            </span>
            <span>
              <span className="nb-cdrawer-steplabel">{s.label}</span>
              {s.hint && <span className="nb-cdrawer-stephint">{s.hint}</span>}
            </span>
          </button>
        ))}
      </div>
      {railNote && <div className="nb-cdrawer-railnote">{railNote}</div>}
    </nav>
  );
}
